import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Phone, CalendarBlank, Truck, WhatsappLogo, Plus, MagnifyingGlass, Package, ChatCircleDots, PencilSimple, Trash,
} from "@phosphor-icons/react";
import { buildWhatsAppUrl, paymentReminderMessage } from "@/lib/whatsapp";
import NayaOrderDialog from "@/components/NayaOrderDialog";
import OrderQuickView from "@/components/OrderQuickView";
import EditOrderDialog from "@/components/EditOrderDialog";
import DispatchDialog from "@/components/DispatchDialog";

const TABS = [
    { key: "pending",    label: "Pending" },
    { key: "dispatched", label: "Dispatched" },
    { key: "all",        label: "Sabhi" },
];

/**
 * Card-grid "Sales Book" view for the Orders page.
 * Props: onCreate() — parent handles the new-order dialog
 */
export default function OrdersCardView({ onCreate }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState("pending");
    const [sales, setSales] = useState([]);
    const [search, setSearch] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [loading, setLoading] = useState(true);
    const [nayaOpen, setNayaOpen] = useState(false);
    const [quickOrder, setQuickOrder] = useState(null);
    const [editOrder, setEditOrder] = useState(null);
    const [dispatchOrder, setDispatchOrder] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/sales", { params: { limit: 500 } });
            setSales(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // filter
    const filtered = sales.filter((o) => {
        if (o.status === "cancelled") return tab === "all";
        if (tab === "pending") return o.status === "draft" || o.status === "pending";
        if (tab === "dispatched") return o.fulfillment_stage === "dispatched" || o.fulfillment_stage === "delivered";
        // all
        return true;
    }).filter((o) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            (o.order_no || "").toLowerCase().includes(s) ||
            (o.customer_name || "").toLowerCase().includes(s) ||
            (o.customer_snapshot?.mobile || "").includes(s)
        );
    }).filter((o) => {
        if (!dateFrom && !dateTo) return true;
        const d = new Date(o.created_at);
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo) {
            const end = new Date(dateTo); end.setHours(23, 59, 59, 999);
            if (d > end) return false;
        }
        return true;
    });

    return (
        <div className="space-y-6" data-testid="orders-card-view">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Sales Book</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Orders</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">Customer orders, advance payment aur dispatch tracking.</p>
                </div>
                <Button onClick={() => setNayaOpen(true)} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="orders-new-btn">
                    <Plus size={16} weight="bold" className="mr-1.5" /> New Order
                </Button>
            </div>

            {/* Pill tabs + filters row */}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="inline-flex items-center gap-1 p-1 rounded-full border border-neutral-200 bg-white shrink-0" data-testid="orders-tabs">
                    {TABS.map((t) => {
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={"px-4 md:px-5 py-1.5 text-xs md:text-sm font-bold uppercase tracking-wider rounded-full transition-colors " +
                                    (active ? "text-white" : "text-neutral-500 hover:text-neutral-900")}
                                style={active ? { backgroundColor: "#15803D" } : {}}
                                data-testid={`orders-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                <div className="relative flex-1 md:max-w-sm">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input placeholder="Customer / mobile / order #" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11" data-testid="orders-search" />
                </div>
                <div className="flex items-center gap-2">
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11 w-40" data-testid="orders-date-from" />
                    <span className="text-xs text-neutral-500 font-semibold">to</span>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11 w-40" data-testid="orders-date-to" />
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500">Loading...</div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-md py-14 text-center bg-white" data-testid="orders-empty">
                    <Package size={40} weight="thin" className="mx-auto text-neutral-300" />
                    <div className="mt-3 text-lg font-bold font-display">No orders here</div>
                    <div className="text-sm text-neutral-500 mt-1">Try another tab or create a new order.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((o) => (
                        <OrderCard
                            key={o.id}
                            order={o}
                            onOpen={() => setQuickOrder(o)}
                            onEdit={() => setEditOrder(o)}
                            onDispatch={() => setDispatchOrder(o)}
                            onDeleted={load}
                        />
                    ))}
                </div>
            )}

            <NayaOrderDialog
                open={nayaOpen}
                onOpenChange={setNayaOpen}
                onCreated={(o) => { load(); navigate(`/orders/${o.id}`); }}
            />

            <OrderQuickView
                order={quickOrder}
                open={!!quickOrder}
                onOpenChange={(v) => { if (!v) setQuickOrder(null); }}
                onChanged={load}
            />

            <EditOrderDialog
                order={editOrder}
                open={!!editOrder}
                onOpenChange={(v) => { if (!v) setEditOrder(null); }}
                onSaved={() => { setEditOrder(null); load(); }}
            />

            <DispatchDialog
                order={dispatchOrder}
                open={!!dispatchOrder}
                onOpenChange={(v) => { if (!v) setDispatchOrder(null); }}
                onDispatched={() => { setDispatchOrder(null); load(); }}
            />
        </div>
    );
}

function OrderCard({ order, onOpen, onEdit, onDispatch, onDeleted }) {
    const c = order.customer_snapshot || {};
    const mobile = c.mobile || order.customer_name === "Walk-in Customer" ? "" : "";
    const disp = order.dispatch || {};
    const dispatchDate = disp.expected_delivery || disp.dispatched_at;

    const paid = Number(order.paid_amount || 0);
    const balance = Number(order.balance_amount ?? (Number(order.total || 0) - paid));

    const isDispatched = order.fulfillment_stage === "dispatched" || order.fulfillment_stage === "delivered";
    const isCancelled = order.status === "cancelled";
    const isEditable = !isCancelled && order.status !== "completed";
    const canDispatch = isEditable && !isDispatched;

    const pillMap = {
        draft:     { bg: "#F3F4F6", fg: "#525252", label: "Draft" },
        pending:   { bg: "#FEE2E2", fg: "#DC2626", label: "Pending" },
        completed: { bg: "#DCFCE7", fg: "#15803D", label: "Completed" },
        cancelled: { bg: "#FEE2E2", fg: "#DC2626", label: "Cancelled" },
    };
    const pill = isDispatched
        ? { bg: "#DBEAFE", fg: "#1D4ED8", label: "Dispatched" }
        : (pillMap[order.status] || pillMap.pending);

    const firstItem = order.items?.[0];
    const extraItems = Math.max(0, (order.items?.length || 0) - 1);

    // Smart CTA: Payment Reminder if money is owed on a dispatched/delivered order, else generic Share
    const isReminder = balance > 0.01 && (isDispatched || order.status === "completed");
    const waMessage = isReminder
        ? paymentReminderMessage({
            customerName: c.name || order.customer_name,
            pendingAmount: balance,
            invoiceNo: order.order_no,
        })
        : (
            `Namaste ${c.name || order.customer_name},\n` +
            `Aapke order ${order.order_no} ki update:\n` +
            `Total: ${formatCurrency(order.total)}\n` +
            `Paid (advance): ${formatCurrency(paid)}\n` +
            `Baaki: ${formatCurrency(balance)}\n` +
            (dispatchDate ? `Dispatch: ${formatDateShort(dispatchDate)}\n` : "") +
            `— BS Fiber Product`
        );
    const waUrl = buildWhatsAppUrl(c.whatsapp || c.mobile, waMessage);
    const ctaLabel = isReminder ? "WhatsApp Payment Reminder" : "WhatsApp Share";
    const CtaIcon = isReminder ? ChatCircleDots : WhatsappLogo;

    return (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 hover:border-neutral-400 transition-colors fade-up flex flex-col"
             data-testid={`order-card-${order.order_no}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <button onClick={onOpen} className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 hover:text-neutral-900" data-testid={`order-card-open-${order.order_no}`}>
                        {order.order_no}
                    </button>
                    <button onClick={onOpen} className="block text-left text-xl font-extrabold font-display tracking-tight leading-tight mt-0.5 hover:underline truncate max-w-full">
                        {c.name || order.customer_name}
                    </button>
                    {c.mobile && (
                        <div className="flex items-center gap-1.5 text-xs text-neutral-600 mt-1">
                            <Phone size={13} weight="duotone" />
                            <span className="tabular">{c.mobile}</span>
                        </div>
                    )}
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded whitespace-nowrap shrink-0"
                      style={{ background: pill.bg, color: pill.fg }}>
                    {pill.label}
                </span>
            </div>

            <div className="mt-3 space-y-1 text-xs text-neutral-600">
                <div className="flex items-center gap-1.5">
                    <CalendarBlank size={13} weight="duotone" className="text-neutral-400" />
                    <span>Order: <b className="text-neutral-900">{formatDateShort(order.created_at)}</b></span>
                </div>
                {dispatchDate && (
                    <div className="flex items-center gap-1.5">
                        <Truck size={13} weight="duotone" className="text-neutral-400" />
                        <span>Dispatch: <b className="text-neutral-900">{formatDateShort(dispatchDate)}</b></span>
                    </div>
                )}
            </div>

            {firstItem && (
                <div className="mt-3 text-xs">
                    <div className="text-neutral-500">{order.items.length} item{order.items.length !== 1 ? "s" : ""}</div>
                    <div className="font-bold text-neutral-900 line-clamp-1">
                        {firstItem.product_name}{extraItems > 0 && <span className="text-neutral-500 font-normal"> +{extraItems} more</span>}
                    </div>
                </div>
            )}

            <div className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-3 gap-2">
                <NumBlock label="Total" value={formatCurrency(order.total)} color="#0A0A0A" />
                <NumBlock label="Advance" value={formatCurrency(paid)} color="#15803D" />
                <NumBlock label="Baaki" value={formatCurrency(balance)} color={balance > 0 ? "#DC2626" : "#15803D"} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                {isEditable && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        className="flex-1 min-w-[80px] h-10 flex items-center justify-center gap-1.5 rounded-full text-sm font-bold border-2 transition-colors hover:bg-neutral-50"
                        style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}
                        data-testid={`order-card-edit-${order.order_no}`}
                    >
                        <PencilSimple size={15} weight="bold" /> Edit
                    </button>
                )}
                {canDispatch && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onDispatch(); }}
                        className="flex-1 min-w-[100px] h-10 flex items-center justify-center gap-1.5 rounded-full text-sm font-bold text-white transition-colors"
                        style={{ backgroundColor: "#15803D" }}
                        data-testid={`order-card-dispatch-${order.order_no}`}
                    >
                        <Truck size={15} weight="bold" /> Dispatch
                    </button>
                )}
                <a href={waUrl} target="_blank" rel="noreferrer"
                   className="flex-1 min-w-[90px] h-10 flex items-center justify-center gap-1.5 rounded-full text-sm font-bold transition-colors"
                   style={{ background: "#DCFCE7", color: "#15803D" }}
                   data-testid={`order-card-whatsapp-${order.order_no}`}>
                    <CtaIcon size={15} weight={isReminder ? "duotone" : "fill"} style={{ color: isReminder ? "#15803D" : "#25D366" }} /> WhatsApp
                </a>
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Order ${order.order_no} ko permanently delete karein? Ye reversal nahi ho sakta.`)) return;
                        try {
                            await api.delete(`/sales/${order.id}`);
                            toast.success(`Order ${order.order_no} deleted`);
                            onDeleted && onDeleted();
                        } catch (err) { toast.error(apiErrorMessage(err)); }
                    }}
                    className="h-10 w-10 flex items-center justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors shrink-0"
                    title="Delete Order"
                    data-testid={`order-card-delete-${order.order_no}`}
                >
                    <Trash size={15} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function NumBlock({ label, value, color }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
            <div className="text-sm font-extrabold tabular font-display mt-0.5" style={{ color }}>{value}</div>
        </div>
    );
}
